import {FC, useMemo, useState} from "react";
import {Issue} from "../api/issue.ts";
import {Column, FlexSpace, Row} from "../atoms/Layout.tsx";
import SelectInput, {Option} from "../atoms/SelectInput.tsx";
import {ExportIcon, SettingsIcon} from "../atoms/Icon";
import IssueListItem from "../organisms/IssueListItem.tsx";
import IssueFilterMenu, {
    DEFAULT_ISSUE_FILTERS,
    doesIssueMatchFilter,
    IssueFilters
} from "../molecules/IssueFilterMenu.tsx";
import {Text} from "../style/typography.tsx";
import Button from "../atoms/Button.tsx";
import {useSite} from "../store/siteContext.ts";
import {exportIssuesCSV} from "../helpers/csv.ts";

interface Props {
    issues: Issue[];
}

type SortValue = 'newest' | 'oldest' | 'highestPriority'

const SORT_OPTIONS: Option[] = [
    {value: 'highestPriority', label: 'Sort by highest priority'},
    {value: 'newest', label: 'Sort by latest'},
    {value: 'oldest', label: 'Sort by oldest'},
]

const IssueList: FC<Props> = ({issues}) => {
    const site = useSite();
    const [sortValue, setSortValue] = useState<SortValue>('highestPriority');
    const [filterValue, setFilterValue] = useState<IssueFilters>(DEFAULT_ISSUE_FILTERS);
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);

    const locations = useMemo<string[]>(() => {
        const uniqueLocations = new Set(issues.map(issue => issue.location).filter(Boolean));
        return Array.from(uniqueLocations);
    }, [issues]);
    const sortedFilteredIssues = useMemo(() => {
        const filteredIssues = issues.filter(issue => doesIssueMatchFilter(issue, filterValue));

        switch (sortValue) {
            case 'highestPriority':
                // Sort by priority primarily, then by creation date within a priority level
                return filteredIssues.sort((a, b) => {
                    const priorityComparison = b.priority.level - a.priority.level; // Higher level first
                    if (priorityComparison !== 0) {
                        return priorityComparison;
                    }
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(); // Then by date
                });
            case 'newest':
                return filteredIssues.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            case 'oldest':
                return filteredIssues.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            default:
                return filteredIssues;
        }
    }, [issues, filterValue]);

    return (
        <Column grow gap="32">
            <Row centerVertically gap="32">
                <SelectInput options={SORT_OPTIONS} value={sortValue} onChange={v => setSortValue(v as SortValue)}/>
                <FlexSpace/>
                <Row centerVertically gap="12">
                    <Button size="medium" variant="outline" onClick={() => exportIssuesCSV(issues, site.name)}>
                        <ExportIcon size={20}/>Export as CSV</Button>
                    <SettingsIcon size={20} onClick={() => setFilterMenuOpen(true)}/>
                </Row>
            </Row>
            <Column grow gap="20">
                {sortedFilteredIssues.length === 0 &&
                    <Text role="body" size="2" weight="medium" center>No issues to display</Text>}
                {sortedFilteredIssues.map(issue => <IssueListItem issue={issue} key={issue.id}/>)}
            </Column>
            <IssueFilterMenu
                open={filterMenuOpen}
                filters={filterValue}
                onChange={setFilterValue}
                onClose={() => setFilterMenuOpen(false)}
                locations={locations}/>
        </Column>
    );
}

export default IssueList;